import {
  Action,
  NonEmptyArray,
  SerializedAssignmentInstance,
  SerializedDueInstance,
  SerializedFormSubmission,
  SerializedStepInstanceStatus,
  SerializedStepNote,
  StepInstanceId,
  Temporal,
} from '@willow/types-iso';

import { StepType } from '../definitions/steps/StepDefinition';
import { Expression } from '../Expressions/AbstractExpressionEvaluator';

export interface SerializedStepInstance<TType extends StepType> {
  id: StepInstanceId;

  type: TType;

  /**
   * We'll probably want these to live in a database where we can set up some
   * scheduled queries to find tasks that need to be activated, tasks that are
   * overdue, etc.
   *
   *     -- find all the steps that need to be activated
   *       SELECT ... FROM steps
   *       WHERE currentStatus = 'inactive'
   *         AND activateAfter < now
   *
   *     -- find all the steps that are overdue
   *       SELECT ... FROM steps
   *       WHERE currentStatus = 'active'
   *         AND dueAt < now;
   */
  dueAt: SerializedDueInstance;

  isVisible: boolean;

  willActivateOn: Temporal.ZonedDateTimeLike | string | undefined;

  canManuallyActivate: boolean;
  canManuallySkip: boolean;
  canManuallyComplete: boolean;
  canManuallyFail: boolean;
  canEditAfterCompletion?: boolean;

  actionsToTake: Action[];

  retryCondition: Expression<'boolean'> | undefined;

  displayInfo: DisplayInfoItem[] | string | undefined;

  /**
   * Things with history (to build into audit logs, etc.)
   */
  statusHistory: NonEmptyArray<SerializedStepInstanceStatus>;
  assignmentHistory: SerializedAssignmentInstance[];
  formSubmissions: SerializedFormSubmission[];
  notes: SerializedStepNote[];

  /**
   * Branded entity IDs allocated by the engine on first activation, keyed by
   * the step definition's `preallocatedEntityRefs` map. Frontend buttons read
   * these to pre-fill the New Event panel; auto-complete expressions read
   * them via `preallocatedEntityRef(stepId, key)`. Stable for the life of the
   * step instance.
   */
  preallocatedEntityRefs?: Record<string, string>;
}

/**
 * A rendered display-info item on a step instance: either markdown text or a
 * reference to a frontend component resolved via the display-component registry.
 */
export type DisplayInfoComponentSlot = { type: 'component'; component: string };
export type DisplayInfoItem = string | DisplayInfoComponentSlot;

export const migrateDisplayInfo = (displayInfo: DisplayInfoItem[] | string | undefined): DisplayInfoItem[] => {
  if (typeof displayInfo === 'string') {
    return [displayInfo];
  }
  if (displayInfo == null) {
    return [];
  }

  return displayInfo.filter(
    (item) => typeof item === 'string' || (typeof item === 'object' && item?.type === 'component'),
  );
};
